// Team session persistence + invite-link parsing.
// The invite link format is `/join/{teamId}#token={rawToken}` — the token lives in the
// URL fragment so it never reaches hosting/server logs.

export const TEAM_ID_KEY = "ds:teamId";

export interface JoinLocation {
  pathname: string;
  hash?: string;
}

export interface JoinSession {
  teamId: string;
  token: string | null;
}

interface TeamStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const safeDecode = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

// parseJoinUrl({ pathname, hash }) -> { teamId, token|null } | null
export function parseJoinUrl(
  loc: JoinLocation | null | undefined,
): JoinSession | null {
  if (!loc || typeof loc.pathname !== "string") return null;
  const m = loc.pathname.match(/^\/join\/([^/]+)\/?$/);
  if (!m) return null;
  const teamId = safeDecode(m[1] ?? "");
  if (!teamId) return null;
  let token = null;
  const hash = typeof loc.hash === "string" ? loc.hash.replace(/^#/, "") : "";
  for (const part of hash.split("&")) {
    if (!part) continue;
    const eq = part.indexOf("=");
    const k = eq >= 0 ? part.slice(0, eq) : part;
    if (k === "token" && eq >= 0) {
      token = safeDecode(part.slice(eq + 1));
      break;
    }
  }
  return { teamId, token };
}

const defaultStorage = (): TeamStorage | null => {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
};

export function loadTeamId(
  storage: TeamStorage | null = defaultStorage(),
): string | null {
  try {
    return storage?.getItem(TEAM_ID_KEY) ?? null;
  } catch {
    return null;
  }
}

export function saveTeamId(
  teamId: string,
  storage: TeamStorage | null = defaultStorage(),
): void {
  try {
    storage?.setItem(TEAM_ID_KEY, teamId);
  } catch {
    /* private mode etc. */
  }
}

export function clearTeamId(
  storage: TeamStorage | null = defaultStorage(),
): void {
  try {
    storage?.removeItem(TEAM_ID_KEY);
  } catch {
    /* ignore */
  }
}
